/** Pulling an artifact's server-rendered export down into the browser.
 *
 *  Two surfaces, two mechanisms, one hook. Outside the embed a plain link is
 *  enough: same origin, session cookie, the browser applies the server's
 *  `Content-Disposition`, and a refusal renders the route's own message in the
 *  opened tab. The embed has no cookie and is cross-origin, so a relative href
 *  would resolve against the HOST page and arrive unauthenticated -- there the
 *  bytes are fetched with the bearer and handed to the downloader instead.
 *
 *  It lives here, rather than inside the card that needed it first, because
 *  ArtifactTableCard and ArtifactDocCard both export through the same route and
 *  the second copy is where a divergence would start. `lib/download.ts` already
 *  records what the copy-paste version of this costs.
 */
import { useCallback, useMemo, useState } from "react";
import { useEmbedApiAuth, makeBearerFetch } from "../../../api/embedApiContext";
import { downloadBlob } from "../../../lib/download";

/** What `GET /artifacts/{id}/export` can render. Mirrors `KNOWN_FORMATS` in
 *  `agent_runtime/documents/renderers/__init__.py` -- the route 400s on
 *  anything else. */
export type ArtifactExportFormat = "html" | "pdf" | "docx" | "pptx" | "xlsx";

const FILENAME_FALLBACK = "artifact";
const FILENAME_MAX_CHARS = 60;

/** The URL the export route answers on.
 *
 *  Relative on purpose for the link path: the SPA is same-origin with the API,
 *  and a relative href is what lets the browser carry the session cookie. The
 *  embed prefixes its API origin onto it. */
export function artifactExportPath(
  artifactId: string,
  fmt: ArtifactExportFormat,
): string {
  return `/artifacts/${artifactId}/export?fmt=${fmt}`;
}

/** The name the saved file gets in the embed, slugged from the artifact title.
 *
 *  Mirrors `_export_filename` in `unified_agent/routes/artifacts_routes.py` --
 *  NFKD fold, drop non-ASCII, collapse everything outside [A-Za-z0-9] to a
 *  hyphen, lowercase, cap the length -- so the file lands under the same name
 *  whichever surface the user downloaded it from.
 *
 *  Derived here rather than read off `Content-Disposition` because the API sets
 *  no CORS `expose_headers`, and the embed is cross-origin: that header is
 *  unreadable to `fetch`. Exposing it would widen what every browser can read
 *  from every response of this API, globally, to settle a filename on one
 *  branch of two components -- and a client would still need this fallback for
 *  any server not yet carrying the change. The SPA never comes through here;
 *  its plain navigation gets the server's name from the browser. */
export function exportFilename(
  title: string | null,
  fmt: ArtifactExportFormat,
): string {
  const ascii = (title ?? "").normalize("NFKD").replace(/[^\x00-\x7F]/g, "");
  const slug = ascii
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, FILENAME_MAX_CHARS)
    .replace(/-+$/, "");
  return `${slug || FILENAME_FALLBACK}.${fmt}`;
}

export interface ArtifactExport {
  /** True when an embed context is present, i.e. bearer rather than cookie. */
  isEmbed: boolean;
  /** A render is in flight. */
  busy: boolean;
  /** The last failure, for the card to show. Cleared on the next attempt. */
  error: string | null;
  /** The href a cookie-authed link should point at. */
  hrefFor: (fmt: ArtifactExportFormat) => string;
  /** Fetch with the bearer and hand the bytes to the browser. Embed only. */
  runEmbedExport: (fmt: ArtifactExportFormat) => Promise<void>;
}

export function useArtifactExport(
  artifactId: string,
  title: string | null,
): ArtifactExport {
  const embed = useEmbedApiAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Same construction as `useAuthedApi`: one bearer-injecting fetch per embed
  // context, resolving the token per call so a refresh is transparent.
  const embedFetch = useMemo(
    () => (embed ? makeBearerFetch(embed.getToken) : null),
    [embed],
  );

  const runEmbedExport = useCallback(
    async (fmt: ArtifactExportFormat) => {
      if (!embed || !embedFetch) return;
      setBusy(true);
      setError(null);
      try {
        const base = embed.apiUrl.replace(/\/+$/, "");
        const response = await embedFetch(`${base}${artifactExportPath(artifactId, fmt)}`);
        if (!response.ok) {
          // Surfaced, not swallowed. The plain-navigation path shows the
          // route's refusal in the opened tab; a fetch whose rejection only
          // reached the console would leave a button that does nothing, which
          // is the worse failure. The status is the actionable part: 409 is
          // "not exportable", 401/403 an expired or wrong token, 404 the route
          // gate. The server's `detail` is model-authored text in the 409 case,
          // so it stays out of the UI.
          setError(`Export failed — the server returned ${response.status}.`);
          return;
        }
        downloadBlob(exportFilename(title, fmt), await response.blob());
      } catch {
        setError("Export failed — could not reach the server.");
      } finally {
        setBusy(false);
      }
    },
    [artifactId, embed, embedFetch, title],
  );

  return {
    isEmbed: embed !== null,
    busy,
    error,
    hrefFor: (fmt) => artifactExportPath(artifactId, fmt),
    runEmbedExport,
  };
}
